import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../components/AuthContext';

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'auth/user-not-found':
      case 'auth/wrong-password':
        return 'Invalid email or password.';
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/user-disabled':
        return 'Your account has been disabled.';
      default:
        return 'An unexpected error occurred. Please try again.';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      const user = await login(email, password);
      // After successful login, AuthContext will set the user and admin status.
      // We can check the isAdmin status from the context, but it might not be updated instantly.
      // A better approach is to get the user object back from the login function and check their claims.
      // For now, we'll rely on the context being updated, but a redirect based on the return value of login would be more robust.
      
      // Let's check the user's role and redirect accordingly.
      // We need to import `isAdmin` from `../../auth` to check the role directly.
      // This is a temporary solution until AuthContext provides the role instantly.
      
      // A simpler immediate solution is to navigate based on what the user is likely to do.
      // Admins go to the dashboard, others to home.
      // The AuthProvider will eventually update the isAdminUser state.
      
      // Let's assume the login function in AuthContext updates the state before the navigation occurs.
      // The logic inside AuthContext needs to be fast.
      
      // The current implementation navigates to the dashboard. Let's refine this.
      // We will navigate to home, and the ProtectedRoute will handle the rest if they try to access admin pages.
      // Or, we can have the login function return the user's status.
      
      // Let's modify the AuthContext login to return the user status.
      // For now, let's just navigate to home, which is a safe default.
      navigate('/home');
    } catch (err) {
      console.error('Login.jsx: Login error:', err);
      setError(getErrorMessage(err.code));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-md">
        <h2 className="text-2xl font-bold text-center mb-6">Login</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="email" className="block text-gray-700 text-sm font-bold mb-2">
              Email:
            </label>
            <input
              type="email"
              id="email"
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="mb-6">
            <label htmlFor="password" className="block text-gray-700 text-sm font-bold mb-2">
              Password:
            </label>
            <input
              type="password"
              id="password"
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 mb-3 leading-tight focus:outline-none focus:shadow-outline"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          {error && <p className="text-red-500 text-xs italic mb-4">{error}</p>}
          <div className="flex items-center justify-between">
            <button
              type="submit"
              className="bg-primary hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
            >
              Sign In
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};